import React from "react";
import "@mdxeditor/editor/style.css";
import "./MarkdownEditorV2.css";
import { debounce } from "lodash";
import { MDXEditor } from "@mdxeditor/editor/MDXEditor";
import { useRef, useState } from "react";

import { toolbarPlugin } from "@mdxeditor/editor/plugins/toolbar";
import { listsPlugin } from "@mdxeditor/editor/plugins/lists";
import { quotePlugin } from "@mdxeditor/editor/plugins/quote";
import { linkPlugin } from "@mdxeditor/editor/plugins/link";
import { linkDialogPlugin } from "@mdxeditor/editor/plugins/link-dialog";
import { UndoRedo } from "@mdxeditor/editor/plugins/toolbar/components/UndoRedo";
import { CreateLink } from "@mdxeditor/editor/plugins/toolbar/components/CreateLink";
import { BlockTypeSelect } from "@mdxeditor/editor/plugins/toolbar/components/BlockTypeSelect";
import { ListsToggle } from "@mdxeditor/editor/plugins/toolbar/components/ListsToggle";
import { BoldItalicUnderlineToggles } from "@mdxeditor/editor/plugins/toolbar/components/BoldItalicUnderlineToggles";
import ConditionalBoldItalicUnderlineToggles from "./MarkdownEditorV2_ConditionalBoldItalicUnderlineToggles";
import { FormControl, FormGroup, Typography } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import FormHelperText from "@material-ui/core/FormHelperText";
import { headingsPlugin } from "@mdxeditor/editor/plugins/headings";

import Picker, { EmojiStyle } from "emoji-picker-react";
import InsertEmoticonIcon from "@material-ui/icons/InsertEmoticon";
import IconButton from "@material-ui/core/IconButton";
import Popover from "@material-ui/core/Popover";
import InfoTooltip from "../InfoTooltip";

const MarkdownEditorV2 = ({
  value,
  placeHolder,
  onChange,
  error,
  helperText,
  maxLength,
  useHeadings = false,
  autoLink = true,
  showInfoTooltip = false, // <-- NEW FLAG (default: false)
  disableBoldItalicUnderline = [], // <-- Array of options to disable: ['bold', 'italic', 'underline']
  disableCreateLink = false, // <-- Flag to disable CreateLink component
  disableListsToggle = false, // <-- Flag to disable ListsToggle component
  disableBlockTypeSelect = false, // <-- Flag to disable BlockTypeSelect component
}) => {
  const [charCount, setCharCount] = useState(value?.length || 0);
  const ref = useRef(null);
  const [emojiAnchor, setEmojiAnchor] = useState(null);

  const handleEmojiOpen = (e) => setEmojiAnchor(e.currentTarget);
  const handleEmojiClose = () => setEmojiAnchor(null);

  const handleEmojiClick = (emojiData) => {
    const current = ref.current?.getMarkdown?.() ?? "";
    const next = constrainText(`${current}${emojiData?.emoji || ""}`);
    ref.current?.setMarkdown(next);
    onChangeEditorSide(next);
    handleEmojiClose();
  };

  const constrainText = (value) => {
    const newText = value?.replace(/<!--[\s\S]*?-->/g, "").trim();
    return newText?.slice(0, maxLength) ?? "";
  };

  const onChangeEditorSide = (newValue) => {
    let cutText = newValue;
    if (maxLength && newValue.length) {
      cutText = newValue.slice(0, maxLength);
      ref.current?.setMarkdown(cutText);
    }
    setCharCount(cutText?.length || 0);
    onChange(cutText);
  };

  const debouncedOnChange = debounce(onChangeEditorSide, 500);

  return (
    <FormControl fullWidth component="fieldset" error={error}>
      <Typography color={error ? "error" : "textPrimary"} variant="subtitle2">
        {placeHolder}
      </Typography>

      <FormGroup>
        <Box minHeight={93}>
          <MDXEditor
            ref={ref}
            contentEditableClassName="custom-mdx-editor"
            markdown={constrainText(value)}
            plugins={[
              listsPlugin(),
              linkPlugin({ disableAutoLink: !autoLink }),
              quotePlugin(),
              linkDialogPlugin(),
              toolbarPlugin({
                toolbarContents: () => (
                  <>
                    {!disableBoldItalicUnderline ||
                    disableBoldItalicUnderline.length === 0 ? (
                      <BoldItalicUnderlineToggles />
                    ) : (
                      <ConditionalBoldItalicUnderlineToggles
                        disableBoldItalicUnderline={disableBoldItalicUnderline}
                      />
                    )}
                    <UndoRedo />

                    <IconButton
                      aria-label="insert-emoji"
                      onClick={handleEmojiOpen}
                    >
                      <InsertEmoticonIcon />
                    </IconButton>

                    {!disableCreateLink && <CreateLink />}
                    {!disableListsToggle && <ListsToggle />}
                    {!disableBlockTypeSelect && <BlockTypeSelect />}
                    {showInfoTooltip && (
                      <InfoTooltip
                        text="Use Shift+Enter to start a new line without starting a new paragraph."
                        title="Insert a line break"
                      />
                    )}
                  </>
                ),
              }),
              ...(useHeadings ? [headingsPlugin()] : []),
            ]}
            onChange={debouncedOnChange}
          />

          <Popover
            anchorEl={emojiAnchor}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
            open={Boolean(emojiAnchor)}
            onClose={handleEmojiClose}
          >
            <Picker
              emojiStyle={EmojiStyle.NATIVE}
              onEmojiClick={handleEmojiClick}
            />
          </Popover>

          {charCount > 0 && (
            <Typography
              style={{
                background: "white",
                borderRadius: 4,
                bottom: 8,
                color:
                  charCount === maxLength
                    ? `rgba(245, 39, 39, 0.8)`
                    : `rgba(0, 0, 0, 0.6)`,
                padding: "2px 4px",
                position: "absolute",
                right: 8,
              }}
              variant="caption"
            >
              {maxLength ? `${charCount}/${maxLength}` : null}
            </Typography>
          )}
        </Box>
      </FormGroup>

      <FormHelperText error={error}>{helperText}</FormHelperText>
    </FormControl>
  );
};

export default MarkdownEditorV2;
